import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import dayjs from "dayjs";
import { CashFlowRequestModel } from "features/cashFlow/data/model/CashFlowRequestModel";
import { CashFlowResponseModel } from "features/cashFlow/data/model/CashFlowResponseModel";
import { CashFlowUseCase } from "features/cashFlow/domain/useCases/CashFlowUseCase";
import { isEmpty } from "lodash";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { CashFlowInjection } from "../../di/CashFlowInjection";
import {
  ExportPayload,
  FetchListInput,
} from "../../domain/entities/CashFlowPayload";

export interface IDropdownActionRecord {
  record: CashFlowResponseModel;
  action: string;
}

export type SavedDetailActionType =
  | CashFlowResponseModel
  | IDropdownActionRecord;
class CashFlowInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public statusLoading = new BehaviorSubject<Record<string, boolean>>({});
  public fetchList = new BehaviorSubject<FetchListInput>({
    kind: "params",
    params: new CashFlowRequestModel(),
  });
  public exportExcel = new BehaviorSubject<ExportPayload | null>(null);
  public fetchDetail = new BehaviorSubject<CashFlowRequestModel>(
    new CashFlowRequestModel(),
  );
  public fetchPartners = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStores = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStaff = new BehaviorSubject<BaseUserRequestModel>({});

  public loadUsers = new BehaviorSubject<BaseUserRequestModel>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.detailLoading.complete();
    this.fetchList.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
    this.fetchPartners.complete();
    this.loadUsers.complete();
  }
}

class CashFlowOutput implements Disposable {
  public savedList = new BehaviorSubject<Array<CashFlowResponseModel>>([]);
  public savedDetail = new BehaviorSubject<SavedDetailActionType>({});
  public pagination = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public savedPartners = new BehaviorSubject<
    Array<BaseFilterListResponseModel>
  >([]);
  public savedStores = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedStaff = new BehaviorSubject<Array<BaseUserResponseModel>>([]);
  public savedUsers = new BehaviorSubject<Array<BaseUserResponseModel>>([]);
  public savedQueryParams = new BehaviorSubject<CashFlowRequestModel | null>(
    null,
  );

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.pagination.complete();
    this.onError.complete();
    this.onExportSuccess.complete();
    this.savedPartners.complete();
    this.savedStores.complete();
    this.savedStaff.complete();
    this.savedUsers.complete();
    this.savedQueryParams.complete();
  }
}

@injectable()
export class CashFlowViewModel extends BaseViewModel<
  CashFlowInput,
  CashFlowOutput
> {
  private cashFlowUseCase: CashFlowUseCase;

  constructor() {
    super(new CashFlowInput(), new CashFlowOutput());
    this.cashFlowUseCase = CashFlowInjection.getUseCase();
  }

  private downloadExcel(data: Blob) {
    const url = window.URL.createObjectURL(data as Blob);
    const link = document.createElement("a");
    const timestamp = dayjs().format("YYYYMMDD_HHmmss");
    link.href = url;
    link.setAttribute("download", `CASH_FLOW_REPORT_${timestamp}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  protected override transform(): void {
    this.skipFirstInitial(
      this.input.fetchList,
      async (input: FetchListInput) => {
        try {
          this.input.loading.next(true);
          let res;
          let paramsToSave: CashFlowRequestModel | null = null;

          switch (input.kind) {
            case "search": {
              const result = await this.cashFlowUseCase.getListFromSearch(
                input.rawFormValues,
                input.context,
              );
              res = result.list;
              paramsToSave = result.params;
              break;
            }
            case "tableChange": {
              const result = await this.cashFlowUseCase.getListWithTableChange(
                input.prevParams,
                input.rawFormValues,
                input.page,
                input.limit,
              );
              res = result.list;
              paramsToSave = result.params;
              break;
            }
            case "redo": {
              const redoResult = await this.cashFlowUseCase.getListRedo();
              res = redoResult.list;
              paramsToSave = redoResult.params;
              break;
            }
            case "params":
            default:
              res = await this.cashFlowUseCase.getList(input.params);
              break;
          }

          if (paramsToSave) {
            this.output.savedQueryParams.next(paramsToSave);
          }

          if (res?.data) {
            this.output.savedList.next(res?.data ?? []);
            this.output.pagination.next(res?.pagination);
          }
        } catch (error) {
          console.error(error);
          this.output.savedList.next([]);
          this.output.onError.next(error);
        } finally {
          this.input.loading.next(false);
        }
      },
    );

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      if (isEmpty(params)) {
        return;
      } else {
        try {
          this.input.detailLoading.next(true);
          const res = await this.cashFlowUseCase.getDetail(params);
          if (res?.data) {
            this.output.savedDetail.next(res?.data);
          }
        } catch (error) {
          console.error(error);
          this.output.onError.next(error);
        } finally {
          this.input.detailLoading.next(false);
        }
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (payload) => {
      if (!payload) return;
      try {
        this.input.loading.next(true);
        const res = await this.cashFlowUseCase.exportReportFromSearch(payload);
        if (res?.data) {
          if (res?.data?.size < 1000) {
            getMessage(strings.GlobalMessage.ExcelError, 3, "error");
            return;
          }

          this.downloadExcel(res?.data);
          this.output.onExportSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchPartners, async (params) => {
      try {
        const cache = productCache.getCache(CacheKeyEnum.partnerList);

        if (!isEmpty(cache)) {
          this.output.savedPartners.next(cache);
          return;
        }
        const res = await this.cashFlowUseCase.getFilterList(params, "partner");
        const data = res ?? [];

        this.output.savedPartners.next(data);

        productCache.saveCache({
          key: CacheKeyEnum.partnerList,
          product: data,
        });
      } catch (error) {
        console.error("Error fetching partners:", error);
        this.output.savedPartners.next([]);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async (params) => {
      const { partnerCode } = params;
      const cacheKey = `${CacheKeyEnum.storeList}-${partnerCode}`;
      const cache = productCache?.getCache(cacheKey);
      if (cache?.length) {
        this.output.savedStores.next(cache);
        return;
      }
      try {
        if (partnerCode) {
          const res = await this.cashFlowUseCase.getFilterList(params, "store");
          this.output.savedStores.next(res);

          productCache.saveCache({
            key: cacheKey,
            product: res,
          });
          return;
        }
      } catch (error) {
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.loadStaff, async (params) => {
      if (isEmpty(params?.storeCode)) {
        this.output.savedStaff.next([]);
        return;
      }
      try {
        const res = await this.cashFlowUseCase.getUserForFilter(
          params,
          "staff",
        );
        if (res) {
          const raw = res ?? [];
          const allUsers = raw.flatMap((item: any) => item?.users ?? []);
          const mappedUsers: BaseUserResponseModel[] = allUsers.map(
            (u: any) => {
              return {
                id: u.id,
                name: u.name || u.userName || u.email || u.phone || "",
                code: u.code,
              };
            },
          );
          this.output.savedStaff.next(mappedUsers);
        }
      } catch (e) {
        this.output.savedStaff.next([]);
        this.output.onError.next(e as Error);
      }
    });

    this.skipFirstInitial(this.input.loadUsers, async (params) => {
      const { providerCode } = params;
      try {
        let res;
        if (providerCode === "b2b") {
          res = await this.cashFlowUseCase.getUserForFilter(params, "userB2B");
        } else if (providerCode === "mobitopup" || providerCode === "smm") {
          res = await this.cashFlowUseCase.getUserForFilter(params, "staff");
        } else {
          res = await this.cashFlowUseCase.getUserForFilter(params, "userB2B");
        }

        const raw = res?.data ?? [];
        const allUsers = raw.flatMap((item: any) => item?.users ?? []);
        const mappedUsers: BaseUserResponseModel[] = allUsers.map((u: any) => {
          return {
            id: u.id,
            name: u.fullName || u.email || u.name || u.phone || "",
            code: u.code,
          };
        });
        this.output.savedUsers.next(mappedUsers);
      } catch (e) {
        this.output.savedUsers.next([]);
        this.output.onError.next(e as Error);
      }
    });
  }
}
